/**
 * Cambiar de negocio tocando su nombre.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ VA EN EL NOMBRE Y NO EN UN MENÚ DE AJUSTES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque es donde la gente ya lo busca. Instagram, Slack, Google Drive: en
 * todos, el nombre de arriba a la izquierda es el conmutador. Nadie tiene que
 * aprender nada.
 *
 * Y porque el nombre del negocio es justamente lo que hay que mirar para saber
 * **dónde estás vendiendo**. Poner ahí el cambio junta la pregunta y la
 * respuesta en el mismo sitio: se ve "Barbería El Corte", se toca, se ve la
 * lista, se cambia.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DOS COSAS QUE NO HACE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. **No aparece si solo hay un negocio.** Un desplegable con una sola opción
 *    es un botón que no hace nada, y aprende a la gente a no tocarlo.
 *
 * 2. **No deja crear negocios a un empleado.** Un cajero pertenece a donde
 *    trabaja; si acabara en varios sitios, la pregunta "¿en qué caja estoy?"
 *    dejaría de tener respuesta obvia justo en el peor momento.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronsUpDown,
  Layers,
  Loader2,
  Plus,
  Store,
  Warehouse,
} from "lucide-react";
import { cx } from "@/components/ui";
import { useCambiarDeSucursal } from "@/components/SucursalActual";
import { useAuth } from "@/stores/auth";
import { useSucursal } from "@/stores/sucursal";
import { negocio } from "@/lib/endpoints";
import { aviso } from "@/stores/ui";
import { mensajeDeError } from "@/lib/api";

export function SelectorNegocio({
  compacto = false,
  onCrearNegocio,
}: {
  compacto?: boolean;
  onCrearNegocio?: () => void;
}) {
  const { tenant, tenants, role, cambiarNegocio, puede } = useAuth();
  const navegar = useNavigate();

  // Estar en «Todos» es un sitio, igual que estar en un negocio: el
  // selector tiene que enseñarlo, o el dueño lee el nombre de la barbería
  // arriba mientras mira las cifras de los cuatro negocios.
  const { pathname } = useLocation();
  const enGrupo = pathname === "/grupo" || pathname.startsWith("/grupo/");
  // Lo mismo con «Todas las sucursales» (2026-09-29): es un sitio, y abajo del
  // nombre dice «Todas las sucursales» en vez de la que estaba elegida.
  const enTodasLasSucursales = pathname === "/sucursales" || pathname.startsWith("/sucursales/");

  const [abierto, setAbierto] = useState(false);
  const [cambiando, setCambiando] = useState<string | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);

  const esDueno = role === "owner";
  const puedeCrear = esDueno && puede("tenant.manage") && Boolean(onCrearNegocio);

  // ═══════════════════════════════════════════════════════════════════════
  //  LAS SUCURSALES VAN AQUÍ Y NO EN OTRO SITIO
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Porque la pregunta es la misma: **¿dónde estoy vendiendo?** Un negocio con
  // dos locales y un selector aparte para el local obliga a mirar dos sitios
  // para responderla, y el día que no coincidan nadie se entera.
  //
  // Se podían crear sucursales desde Ajustes y no había forma de entrar en
  // ellas: se creaban y ahí se quedaban. El backend siempre aceptó la sucursal
  // al vender —usaba la principal cuando no se la mandaban— pero ninguna
  // pantalla se la mandaba nunca.
  const elegida = useSucursal((s) => s.porNegocio[tenant?.id ?? ""] ?? null);
  const { cambiar: cambiarDeSucursal, aplicar: corregirSucursal } = useCambiarDeSucursal();

  const { data: sucursales } = useQuery({
    queryKey: ["sucursales", tenant?.id],
    queryFn: negocio.sucursales,
    enabled: Boolean(tenant?.id),
    staleTime: 5 * 60 * 1000,
  });

  const locales = (sucursales ?? []).filter((l) => l.is_active);
  const hayVariosLocales = locales.length > 1;
  // Los almacenes van en su propio apartado (lo pidió el dueño, 2026-10-01).
  const soloSucursales = locales.filter((l) => l.tipo !== "almacen");
  const soloAlmacenes = locales.filter((l) => l.tipo === "almacen");
  // «Todas» es para quien ve los reportes: el cajero trabaja en UNA sucursal.
  // Y junta sucursales: los almacenes no venden.
  const puedeVerTodas = soloSucursales.length > 1 && puede("reports.sucursales");
  const localActual =
    locales.find((l) => l.id === elegida) ??
    locales.find((l) => l.is_default) ??
    locales[0] ??
    null;

  // ⚠️ La lista trae SOLO las sucursales de esta persona (2026-09-29). Si el
  // aparato tenía guardada otra —se la quitaron en Equipo, o la tablet era de
  // otra sucursal—, se pasa a una de las suyas: con la vieja, al cobrar, el
  // servidor diría «No tienes acceso a esa sucursal».
  useEffect(() => {
    if (!tenant || !sucursales || locales.length === 0) return;
    const guardadaNoEsSuya = elegida !== null && !locales.some((l) => l.id === elegida);
    const sinPrincipal = elegida === null && !locales.some((l) => l.is_default);
    if (!guardadaNoEsSuya && !sinPrincipal) return;
    corregirSucursal(locales[0]!.id);
  }, [tenant, sucursales, locales, elegida, corregirSucursal]);

  /**
   * Una sucursal o un almacén en el menú: la misma fila en los dos apartados.
   *
   * ⚠️ Entrar a un almacén lleva a su inventario —su barra no tiene «Inicio»
   * ni «Vender»—, y salir de uno, o de «Todas», lleva al Inicio de la
   * sucursal (2026-10-01).
   */
  const filaDeLocal = (l: (typeof locales)[number]) => {
    // Mirando «Todas», ninguna sucursal está marcada: no se está en ninguna en
    // concreto.
    const activo = !enTodasLasSucursales && l.id === localActual?.id;
    const esAlmacen = l.tipo === "almacen";
    return (
      <button
        key={l.id}
        type="button"
        role="menuitem"
        onClick={() => {
          setAbierto(false);
          // Cada sucursal tiene su catálogo y sus cifras: lo de la otra se
          // vacía, y con una venta en curso se pregunta antes. Ver
          // `useCambiarDeSucursal`.
          cambiarDeSucursal({ id: l.id, nombre: l.name }, () => {
            if (esAlmacen) navegar("/inventario");
            else if (enTodasLasSucursales || localActual?.tipo === "almacen") navegar("/");
            aviso.exito(esAlmacen ? `Trabajando en ${l.name}` : `Vendiendo en ${l.name}`);
          });
        }}
        className={cx(
          "pulsable flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left",
          activo ? "bg-accent-soft" : "hover:bg-surface-hover",
        )}
      >
        <span
          className={cx(
            "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
            activo ? "bg-accent text-on-accent" : "bg-surface-2 text-muted",
          )}
        >
          {esAlmacen ? <Warehouse size={15} /> : <Store size={15} />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{l.name}</span>
          <span className="block truncate text-[11px] text-muted">
            {l.address || (l.is_default ? "Sucursal principal" : "Sin dirección")}
          </span>
        </span>

        {activo && <Check size={15} className="shrink-0 text-accent" />}
      </button>
    );
  };

  // Con un solo negocio y sin poder crear otro, esto no sería un menú: sería un
  // botón decorativo. Se devuelve el nombre a secas.
  // Con varias sucursales el menú hace falta aunque haya un solo negocio: es
  // el único sitio desde el que se puede cambiar de local.
  const hayMenu = tenants.length > 1 || puedeCrear || hayVariosLocales;

  useEffect(() => {
    if (!abierto) return;

    function fuera(e: MouseEvent) {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    }
    function escape(e: KeyboardEvent) {
      if (e.key === "Escape") setAbierto(false);
    }

    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", escape);
    };
  }, [abierto]);

  async function ir(id: string) {
    if (id === tenant?.id) {
      setAbierto(false);
      return;
    }

    setCambiando(id);
    try {
      await cambiarNegocio(id);
      setAbierto(false);
      // Se vuelve al inicio a propósito. Si te quedaras en /inventario al
      // cambiar de negocio, verías el inventario del otro sitio y tardarías un
      // rato en darte cuenta de que estás mirando otra cosa.
      navegar("/");
    } catch (e) {
      aviso.error(mensajeDeError(e));
    } finally {
      setCambiando(null);
    }
  }

  // ⚠️ En «Todos» el título es «Todos», no el nombre del negocio.
  //
  // Sin esto el dueño lee «Barbería El Corte» arriba mientras la pantalla le
  // enseña las cifras de sus cuatro negocios, y lo primero que piensa es que
  // las cifras son de la barbería.
  const nombre = enGrupo ? "Todos" : (tenant?.name ?? "Softfi");

  if (!hayMenu) {
    return (
      <div className="min-w-0">
        <p
          className={cx(
            "truncate font-semibold leading-tight tracking-[-0.01em] text-ink",
            compacto ? "text-sm" : "text-sm",
          )}
        >
          {nombre}
        </p>
        <p
          className={cx("truncate leading-tight text-muted", compacto ? "text-[11px]" : "text-xs")}
        >
          {etiqueta(role)}
        </p>
      </div>
    );
  }

  return (
    <div ref={contenedor} className="relative min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className={cx(
          "pulsable group flex w-full min-w-0 items-center gap-1.5 rounded-[var(--radius-control)]",
          "-mx-1.5 px-1.5 py-1 text-left hover:bg-surface-hover",
          abierto && "bg-surface-hover",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span
              className={cx(
                "truncate font-semibold leading-tight tracking-[-0.01em] text-ink",
                "text-sm",
              )}
            >
              {nombre}
            </span>
            <ChevronsUpDown
              size={13}
              className={cx(
                "shrink-0 text-faint transition-transform group-hover:text-muted",
                abierto && "rotate-180",
              )}
            />
          </span>
          {/*
            Con varios locales, aquí va el LOCAL y no el rol. Es lo que de
            verdad hay que poder mirar de un vistazo antes de cobrar: el rol no
            cambia nunca, el local sí, y equivocarse de local mete la venta y el
            inventario en el sitio equivocado.
          */}
          <span
            className={cx(
              "block truncate leading-tight",
              hayVariosLocales ? "font-medium text-accent" : "text-muted",
              compacto ? "text-[11px]" : "text-xs",
            )}
          >
            {enGrupo
              ? `${tenants.length} ${tenants.length === 1 ? "negocio" : "negocios"}`
              : enTodasLasSucursales && hayVariosLocales
                ? "Todas las sucursales"
                : hayVariosLocales && localActual
                ? localActual.name
                : etiqueta(role)}
          </span>
        </span>
      </button>

      {abierto && (
        <div
          role="menu"
          className={cx(
            "animar-entrada absolute left-0 top-full z-50 mt-1.5 w-[min(20rem,calc(100vw-2rem))]",
            "overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface",
            "shadow-[var(--shadow-pop)]",
          )}
        >
          {/* ═══════════ TODOS JUNTOS ═══════════ */}
          {/*
            Va ARRIBA de la lista y no al final, porque con tres o cuatro
            negocios es lo primero que se busca por la mañana: cómo fue el día
            en general, antes de entrar a mirar ninguno en concreto.

            No lleva candado aquí aunque sea de Premium: la pantalla de destino
            enseña el muro con lo que se está perdiendo, que convence mucho más
            que un candado en un menú.
          */}
          {tenants.length > 1 && (
            <div className="p-1.5 pb-0">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAbierto(false);
                  navegar("/grupo");
                }}
                className={cx(
                  "pulsable flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left",
                  enGrupo ? "bg-accent-soft" : "hover:bg-surface-hover",
                )}
              >
                <span
                  className={cx(
                    "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                    enGrupo ? "bg-accent text-on-accent" : "bg-accent-soft text-accent",
                  )}
                >
                  <Layers size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink">Todos</span>
                  <span className="block truncate text-[11px] text-muted">
                    Los {tenants.length} juntos, con sus cifras
                  </span>
                </span>
                {/* La misma palomita que un negocio, porque es lo mismo: un
                    sitio donde se puede estar. */}
                {enGrupo && <Check size={15} className="shrink-0 text-accent" />}
              </button>
            </div>
          )}

          {tenants.length > 1 && (
            <>
              <p className="rotulo px-3 pt-2.5">Tus negocios</p>
              <div className="lista-escalonada max-h-72 overflow-y-auto scroll-fino p-1.5">
                {tenants.map((t) => {
                  const activo = t.id === tenant?.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="menuitem"
                      onClick={() => void ir(t.id)}
                      disabled={cambiando !== null}
                      className={cx(
                        "pulsable flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left",
                        activo ? "bg-accent-soft" : "hover:bg-surface-hover",
                        cambiando !== null && !activo && "opacity-50",
                      )}
                    >
                      {/* Las iniciales dan un ancla visual: con tres negocios de
                          nombre parecido, el color y las letras se reconocen
                          antes que el texto. */}
                      <span
                        className={cx(
                          "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                          "text-[11px] font-bold",
                          activo ? "bg-accent text-on-accent" : "bg-surface-2 text-muted",
                        )}
                      >
                        {t.name.slice(0, 2).toUpperCase()}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ink">
                          {t.name}
                        </span>
                        <span className="block truncate text-[11px] text-muted">
                          {etiqueta(t.role)}
                        </span>
                      </span>

                      {cambiando === t.id ? (
                        <Loader2 size={15} className="shrink-0 animate-spin text-accent" />
                      ) : activo ? (
                        <Check size={15} className="shrink-0 text-accent" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ═══════════ SUCURSALES ═══════════ */}
          {/*
            Solo cuando hay más de uno. Con un local, un selector de local es un
            botón que no hace nada — y enseñar sucursales a quien no las usa es
            decirle que le falta configurar algo que no le hace falta.
          */}
          {hayVariosLocales && (
            <>
              {/* «Sucursales» y no «En qué local estás»: más formal (lo pidió el
                  dueño, 2026-09-29), y ya no es solo «dónde estoy»: arriba
                  está «Todas». */}
              <p className="rotulo px-3 pt-2.5">Sucursales</p>
              <div className="lista-escalonada max-h-72 overflow-y-auto scroll-fino p-1.5">
                {/*
                  ⚠️ «TODAS» ARRIBA, COMO «TODOS» EN LOS NEGOCIOS (2026-09-29).

                  Antes, al final, había un «Comparar mis N locales» que llevaba
                  a una sola pantalla. El dueño pidió un «Todas» con su propio
                  apartado: ventas, dinero e inventario de todas juntas, cada
                  una al lado de la otra. Es un sitio donde se está, y por eso
                  lleva la palomita como una sucursal.
                */}
                {puedeVerTodas && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAbierto(false);
                      navegar("/sucursales");
                    }}
                    className={cx(
                      "pulsable flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left",
                      enTodasLasSucursales ? "bg-accent-soft" : "hover:bg-surface-hover",
                    )}
                  >
                    <span
                      className={cx(
                        "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                        enTodasLasSucursales ? "bg-accent text-on-accent" : "bg-accent-soft text-accent",
                      )}
                    >
                      <Building2 size={15} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink">Todas</span>
                      <span className="block truncate text-[11px] text-muted">
                        Las {soloSucursales.length} juntas, con sus cifras
                      </span>
                    </span>
                    {enTodasLasSucursales && <Check size={15} className="shrink-0 text-accent" />}
                  </button>
                )}
                {soloSucursales.map(filaDeLocal)}
              </div>

              {/* ═══════════ ALMACENES, EN SU APARTADO (2026-10-01) ═══════════ */}
              {soloAlmacenes.length > 0 && (
                <>
                  <p className="rotulo border-t border-line px-3 pt-2.5">Almacenes</p>
                  <div className="max-h-48 overflow-y-auto scroll-fino p-1.5">
                    {soloAlmacenes.map(filaDeLocal)}
                  </div>
                </>
              )}
            </>
          )}

          {puedeCrear && (
            <div className={cx("p-1.5", (tenants.length > 1 || hayVariosLocales) && "border-t border-line")}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAbierto(false);
                  onCrearNegocio?.();
                }}
                className="pulsable flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left hover:bg-surface-hover"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-dashed border-line-strong text-muted">
                  <Plus size={15} />
                </span>
                <span className="text-sm font-medium text-ink">Crear otro negocio</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function etiqueta(role: string | null | undefined): string {
  if (role === "owner") return "Dueño";
  // "Supervisor" y no "Encargado": es como se llama el rol en el resto de la
  // aplicación, y dos nombres para lo mismo hacen dudar de si son lo mismo.
  if (role === "admin") return "Supervisor";
  if (role === "employee") return "Empleado";
  return "—";
}
